import { Button, Collapse, Drawer, Empty, Tooltip } from "antd";
import {
  IEventWithActiveZoneListings,
  IMarketListingZone,
} from "models/interfaces";
import React, { useCallback, useEffect, useState } from "react";
import { ZonesService } from "services/SkyboxServices";
import { LoggerService } from "sharedServices";
import { twoDecimalPlacesNumber } from "utils/commonFunction";
import { formatDateGivenFormatString } from "utils/formatTimePeriod";
import LoadingSpinner from "utils/sharedComponents/LoadingSpinner";

export const MARKET_LISTINGS_DRAWER_WIDTH = 600;

interface IOwnListingForHighlight {
  section: string;
  row: string;
  quantity: number;
}

interface IMarketListingsDrawer {
  event: IEventWithActiveZoneListings;
  ourListings?: IOwnListingForHighlight[];
  visible: boolean;
  onClose: () => void;
}

const MarketListingsDrawer: React.FC<IMarketListingsDrawer> = ({
  event,
  ourListings,
  visible,
  onClose,
}) => {
  const eventId = event.event_id;
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [marketListings, setMarketListings] = useState<IMarketListingZone[]>(
    []
  );

  const fetchMarketListings = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      try {
        setIsLoading(true);
        setHasError(false);
        const response = await new ZonesService().getMarketListingByEvent(
          eventId
        );
        if (isCancelled()) return;
        setMarketListings(response.data.data?.market_listings ?? []);
      } catch (error) {
        if (isCancelled()) return;
        setMarketListings([]);
        setHasError(true);
        await new LoggerService().log({
          payload: error,
          function_name: "fetchMarketListings",
        });
      } finally {
        if (!isCancelled()) setIsLoading(false);
      }
    },
    [eventId]
  );

  // refetched on every open so the drawer always reflects the latest scrape
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    fetchMarketListings(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [visible, fetchMarketListings]);

  // Same canonicalization convention the backend uses (trim(upper(...))) so
  // a match here means the same thing a match in the DB would.
  const canon = (value: string | null | undefined) =>
    (value ?? "").trim().toUpperCase();

  const ownListingKeys = new Set(
    (ourListings ?? []).map(
      (listing) =>
        `${canon(listing.section)}|${canon(listing.row)}|${listing.quantity}`
    )
  );

  const renderBody = () => {
    // Tracks keys already highlighted so a duplicate scraped listing (same
    // section/row/qty appearing twice -- a known pre-existing data quirk)
    // only lights up its first occurrence, not every one.
    const alreadyHighlighted = new Set<string>();
    const isOwnListing = (
      section: string,
      row: string | null,
      qty: number | null
    ) => {
      const key = `${canon(section)}|${canon(row)}|${qty}`;
      if (!ownListingKeys.has(key) || alreadyHighlighted.has(key)) {
        return false;
      }
      alreadyHighlighted.add(key);
      return true;
    };

    if (isLoading) {
      return <LoadingSpinner isLoading={true} />;
    }
    if (hasError) {
      return (
        <Empty description="Unable to fetch market listings">
          <Button onClick={() => fetchMarketListings()}>Retry</Button>
        </Empty>
      );
    }
    if (!marketListings.length) {
      return <Empty description="No market listings found" />;
    }
    return (
      <Collapse className="marketListingsCollapse">
        {marketListings.map((zone, zoneIndex) => (
          <Collapse.Panel
            key={`${zone.zone_section}-${zoneIndex}`}
            header={zone.zone_section}
          >
            <table className="marketListingsTable">
              <thead>
                <tr>
                  <th>Row</th>
                  <th className="textRight">Qty</th>
                  <th className="textRight">Price</th>
                  <th>Rating</th>
                  <th>Badges</th>
                </tr>
              </thead>
              <tbody>
                {zone.seat_structure.map((seat, seatIndex) => (
                  <tr
                    key={seatIndex}
                    className={
                      isOwnListing(zone.zone_section, seat.row, seat.qty)
                        ? "ownListingRow"
                        : undefined
                    }
                  >
                    <td>{seat.row ?? "-"}</td>
                    <td className="textRight">{seat.qty ?? "-"}</td>
                    <td className="textRight">
                      {seat.price === null || seat.price === undefined
                        ? "-"
                        : `$${twoDecimalPlacesNumber(Number(seat.price))}`}
                    </td>
                    <td>
                      {seat.rating
                        ? `${seat.rating}${
                            seat.rating_desc ? ` - ${seat.rating_desc}` : ""
                          }`
                        : "-"}
                    </td>
                    <td>
                      {seat.badges?.length
                        ? seat.badges.map((badge, badgeIndex) => (
                            <div key={badgeIndex}>{badge.title ?? "-"}</div>
                          ))
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Collapse.Panel>
        ))}
      </Collapse>
    );
  };

  return (
    <Drawer
      className="customDrawer dark"
      placement="right"
      closable={false}
      mask={false}
      width={MARKET_LISTINGS_DRAWER_WIDTH}
      onClose={onClose}
      visible={visible}
    >
      <div className="marketListingsStickyHeader">
        <div className="drawerHeader">
          <h5>MARKET LISTINGS</h5>
          <div>
            <Tooltip title="Close Market Listings" placement="left">
              <Button onClick={onClose}>
                <i className="da icon-arrow-right-lite"></i>
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className="marketListingsEventInfo">
          <strong>{event.event_name}</strong>
          <div>
            {event.venue_name}
            {event.venue_location ? `: ${event.venue_location}` : ""}
          </div>
          <div>
            {formatDateGivenFormatString(
              event.event_date,
              "ddd, MMM DD, YYYY h:mm A"
            )}
          </div>
        </div>
      </div>
      {renderBody()}
    </Drawer>
  );
};

export default MarketListingsDrawer;
